import React, { useEffect, FC } from 'react'
import { useIntl } from 'react-intl'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { css } from '@emotion/css'
import { toast } from '@contrast-security-inc/design-system-alerts'
import {
  IconHelp,
  theme
} from '@contrast-security-inc/design-system-foundations'
import {
  Link,
  Text,
  TextProps
} from '@contrast-security-inc/design-system-typography'
import { useJiraIntegrationPage } from './jira-integration-page.provider'
import { JiraFormFieldTypes } from './jira-form-field-types'
import { JiraIntegrationForm } from './jira-form-schema'
import { AddIntegrationRowLink } from './two-way-integration/add-integration-row-link'
import { IntegrationRows } from './two-way-integration/integration-rows'
import { useJiraTwoWayUrlQuery } from '../api/use-jira-two-way-url-query'
import { useProjectStatusesQuery } from '../api/use-project-statuses-query'
import { useResolutionsQuery } from '../api/use-resolutions-query'
import { transformFormDataToBugtrackerRequest } from '../../bugtrackers/api/build-api-payload-bugtracker-credentials'
import { Box, Row } from '../../../../legacy-design-system/form'
import { FormCheckbox } from '../../../../legacy-design-system/checkbox'
import { CopyButton, WithTooltip } from '../../../../design-system'
import { Label } from '../../../../legacy-design-system/form-label'
import { theme as deprecatedTheme } from '../../../../styled'

const integrationUrlProps: TextProps = {
  font: 'metadata',
  color: 'neutral80'
}

export const TwoWayIntegration: FC<TextProps> = () => {
  const {
    state: { organizationUuid, bugtrackerId }
  } = useJiraIntegrationPage()
  const { watch, control, getValues, setValue } =
    useFormContext<JiraIntegrationForm>()
  const watchIntegration = watch(JiraFormFieldTypes.INTEGRATION)
  const watchProject = watch(JiraFormFieldTypes.PROJECT)
  const watchIssueType = watch(JiraFormFieldTypes.ISSUE_TYPE)
  const watchURL = watch(JiraFormFieldTypes.URL)
  const { formatMessage } = useIntl()

  const { fields, append, replace, update, remove } = useFieldArray<
    JiraIntegrationForm,
    'integrations'
  >({
    control,
    name: 'integrations'
  })

  const { url } = useJiraTwoWayUrlQuery({
    organizationUuid,
    integration: watchIntegration,
    bugtrackerId
  })

  useEffect(() => {
    // need to store the generated webhook URL in the form, the backend requires it for new integrations
    if (!watchURL) {
      setValue(JiraFormFieldTypes.URL, url)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, watchURL])

  const { resolutions } = useResolutionsQuery({
    organizationUuid,
    bugtrackerRequest: transformFormDataToBugtrackerRequest(getValues),
    enabled: !!watchIntegration
  })

  const { statuses } = useProjectStatusesQuery({
    organizationUuid,
    bugtrackerRequest: transformFormDataToBugtrackerRequest(getValues),
    projectName:
      watchProject && typeof watchProject === 'object' ? watchProject.key : '',
    enabled: !!watchIntegration
  })

  const addIntegration = () => {
    append({
      issue_status: '',
      issue_resolution: undefined,
      trace_status: '',
      trace_substatus: undefined
    })
  }

  useEffect(() => {
    if (watchIntegration && fields.length < 1) {
      append({
        issue_status: '',
        issue_resolution: undefined,
        trace_status: '',
        trace_substatus: undefined
      })
    }
    if (!watchIntegration && fields.length) {
      replace([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchIntegration])

  return watchProject && watchIssueType ? (
    <Row>
      <Box basis={12}>
        <div
          className={css`
            display: flex;
            gap: 4px;
          `}
        >
          <FormCheckbox
            name={JiraFormFieldTypes.INTEGRATION}
            label={formatMessage({
              id: 'action_integrationsSettings_enableTwoWayIntegration'
            })}
          />
          <WithTooltip
            tiny
            placement="auto"
            renderTooltipContent={() => (
              <>
                <Text font="bodyStrong">
                  {formatMessage({
                    id: 'title_integrationsSettings_twoWayIntegration'
                  })}
                </Text>
                <br />
                <Text font="body">
                  {formatMessage({
                    id: 'detail_integrationsSettings_jiraTwoWayIntegration'
                  })}
                </Text>
                <br />
                <Text font="body">
                  {formatMessage(
                    {
                      id: 'variable_integrationsSettings_twoWayIntegrationGetStarted_react'
                    },
                    {
                      a: (msg) => (
                        <Link
                          href="https://developer.atlassian.com/jiradev/jira-apis/webhooks#Webhooks-rest"
                          isExternal={true}
                        >
                          {msg}
                        </Link>
                      )
                    }
                  )}
                </Text>
              </>
            )}
            children={() => <IconHelp size="sm" color="neutral50" />}
          />
        </div>
        {/* INT-893 when adding the remove and add row, chekcing the box should add a blank row and reverse true */}
        {watchIntegration && url && (
          <>
            <Text
              data-e2e="two-way-integration-url"
              className={css`
                padding-right: 24px;
              `}
              {...integrationUrlProps}
            >
              {url}
            </Text>
            <CopyButton
              stringToCopy={url}
              onClick={() => toast(formatMessage({ id: 'title_success' }))}
            />
            <Row
              className={css`
                margin-left: 0;
                gap: 4px;
              `}
            >
              <Box
                basis={7}
                className={css`
                  display: flex;
                  flex-direction: row;
                  margin-left: 0;
                  gap: 4px;
                `}
              >
                <Label>
                  {formatMessage({
                    id: 'title_integrationsSettings_vulnerabilityStatus'
                  })}
                </Label>

                <Label
                  className={css`
                    color: ${theme.color.neutral60};
                    font-weight: ${deprecatedTheme.fontWeights.primary.regular};
                  `}
                >
                  {formatMessage({
                    id: 'detail_integrationsSettings_statusBasedOnJiraTicket'
                  })}
                </Label>
              </Box>

              <Label className={css``}>
                {formatMessage({
                  id: 'title_vulnerabilityReason'
                })}
              </Label>
            </Row>
            <IntegrationRows
              fields={fields}
              resolutions={resolutions}
              statuses={statuses}
              remove={remove}
              update={update}
            />
            <AddIntegrationRowLink addIntegration={addIntegration} />
          </>
        )}
      </Box>
    </Row>
  ) : null
}
